<template>
  <b-modal ref="add-attachment-modal"
           :modal-class="v2ModalClass"
           size="lg"
           @hide="reset"
  >
    <template #modal-header>
      <div class="" style="width: 100%;">
        <span @click="hide" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
          <font-awesome-icon icon="times"/>
        </span>
        <h5 class="mb-1 text-center" qid="add-attachment-title">{{ $t('tasks.add_attachment.title') }}</h5>
      </div>
    </template>

    <b-alert v-if="error.length" show variant="danger">{{ error }}</b-alert>

    <p class="text-muted">{{ $t('tasks.add_attachment.description') }}</p>

    <!-- 1. Suggested attachments from linked controls -->
    <div class="add-attachment__section" qid="add-attachment-suggested">
      <div class="add-attachment__section-title">
        <font-awesome-icon icon="sparkles" class="mr-1"/>{{ $t('tasks.add_attachment.suggested') }}
      </div>
      <b-form-checkbox v-for="file in suggestedFiles"
                       :key="'suggested-' + file.id"
                       v-model="selectedIds"
                       :value="file.id"
                       class="add-attachment__file"
      >
        <a href="#" @click.prevent.stop="openDocument(file)">{{ file.file_name }}</a>
      </b-form-checkbox>
      <div v-if="!suggestedFiles.length" class="text-muted small">{{ $t('tasks.add_attachment.no_suggestions') }}</div>
    </div>

    <!-- 2. Evidence library of the engagement -->
    <div class="add-attachment__section" qid="add-attachment-library">
      <div class="add-attachment__section-title">
        <font-awesome-icon icon="folder-open" class="mr-1"/>{{ $t('tasks.add_attachment.library') }}
      </div>
      <b-form-input v-if="libraryFiles.length > 6"
                    v-model="search"
                    size="sm"
                    class="mb-2"
                    :placeholder="$t('projects.engagement.documents.search_placeholder')"
      ></b-form-input>
      <div v-if="loadingLibrary" class="text-muted small"><font-awesome-icon icon="spinner" class="fa-spin"/></div>
      <b-form-checkbox v-for="file in visibleLibraryFiles"
                       :key="'library-' + file.id"
                       v-model="selectedIds"
                       :value="file.id"
                       class="add-attachment__file"
      >
        <a href="#" @click.prevent.stop="openDocument(file)">{{ file.file_name }}</a>
        <span class="text-muted small ml-1">
          · {{ $t('projects.engagement.evidence.linked_count', { count: file.linked_task_ids.length + (file.owner_task ? 1 : 0) }) }}
        </span>
      </b-form-checkbox>
      <div v-if="!loadingLibrary && !libraryFiles.length" class="text-muted small">{{ $t('tasks.add_attachment.no_library_files') }}</div>
    </div>

    <!-- 3. Upload a new file -->
    <div class="add-attachment__section add-attachment__section--upload" qid="add-attachment-upload">
      <div class="add-attachment__section-title">
        <font-awesome-icon icon="cloud-upload-alt" class="mr-1"/>{{ $t('tasks.add_attachment.upload_new') }}
      </div>
      <div class="add-attachment__upload-options">
        <file-upload ref="evidence-upload"
                     v-model="evidenceFiles"
                     input-id="add-attachment-evidence"
                     :multiple="true"
                     @input="uploadEvidence"
                     class="btn btn-secondary btn-sm"
                     qid="add-attachment-upload-evidence"
        >
          {{ $t('tasks.add_attachment.upload_evidence') }}
        </file-upload>
        <file-upload v-if="mode === 'main'"
                     ref="working-paper-upload"
                     v-model="workingPaperFiles"
                     input-id="add-attachment-working-paper"
                     :multiple="true"
                     @input="uploadWorkingPaper"
                     class="btn btn-secondary btn-sm"
                     qid="add-attachment-upload-working-paper"
        >
          {{ $t('tasks.add_attachment.upload_working_paper') }}
        </file-upload>
      </div>
      <small class="form-text text-muted">
        {{ mode === 'main' ? $t('tasks.add_attachment.upload_hint_main') : $t('tasks.add_attachment.upload_hint_data_collection') }}
      </small>
    </div>

    <template #modal-footer>
      <div class="" style="width: 100%;">
        <div class="alert alert-menu-color color-secondary mb-0" role="alert">
          <div class="text-right">
            <b-button variant="secondary" @click="hide">{{ $t('projects.create.cancel') }}</b-button>
            <b-button class="ml-3"
                      variant="success"
                      :disabled="buttonDisabled || !selectedIds.length"
                      @click="linkSelected"
                      qid="add-attachment-link-selected"
            >
              <span v-if="buttonDisabled" class="mr-1"><font-awesome-icon icon="spinner" size="sm" class="fa-spin"/></span>
              {{ $t('tasks.add_attachment.link_selected', { count: selectedIds.length }) }}
            </b-button>
          </div>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import assessmentSingleTaskProxy from './assessmentSingleTaskProxy';
import piincHttp from '@/util/configureAxios.js';
import { openDocument as openDocumentInTab } from '@/util/openDocument.js';

// Add attachment on engagement tasks: reuse suggested and evidence library files before uploading a new file
export default {
  name: 'AssessmentGlobalSingleTaskModalAddAttachment',
  mixins: [assessmentSingleTaskProxy],
  data() {
    return {
      mode: 'data_collection',
      libraryFiles: [],
      loadingLibrary: false,
      selectedIds: [],
      search: '',
      evidenceFiles: [],
      workingPaperFiles: [],
      buttonDisabled: false,
      error: ''
    };
  },
  computed: {
    // Files the task already has, uploaded to it or linked
    taskFileIds() {
      const task = this.selectedTask || {};
      return _.map(task.media || [], 'id').concat(_.map(task.linked_media || [], 'id'));
    },
    suggestedFiles() {
      const files = this.mode === 'main' ? this.allLinkedMediaFiles : this.allLinkedDataMediaFiles;
      return _.uniqBy(_.filter(files || [], file => !this.taskFileIds.includes(file.id)), 'id');
    },
    availableLibraryFiles() {
      const suggestedIds = _.map(this.suggestedFiles, 'id');
      return _.filter(this.libraryFiles, file => !this.taskFileIds.includes(file.id) && !suggestedIds.includes(file.id));
    },
    visibleLibraryFiles() {
      const search = this.search.trim().toLowerCase();
      return search ? _.filter(this.availableLibraryFiles, file => file.file_name.toLowerCase().includes(search)) : this.availableLibraryFiles;
    }
  },
  methods: {
    show(mode) {
      this.mode = mode;
      this.reset();
      this.loadLibrary();
      this.$refs['add-attachment-modal'].show();
    },
    hide() {
      this.$refs['add-attachment-modal'].hide();
    },
    reset() {
      this.selectedIds = [];
      this.search = '';
      this.error = '';
      this.evidenceFiles = [];
      this.workingPaperFiles = [];
    },
    loadLibrary() {
      let self = this;
      this.loadingLibrary = true;
      piincHttp.get('tasks/' + this.selectedTask.id + '/evidence').then(function(response) {
        self.libraryFiles = response.data.files;
        self.loadingLibrary = false;
      }, function() {
        self.loadingLibrary = false;
      });
    },
    openDocument(file) {
      openDocumentInTab(file);
    },
    done(message) {
      this.buttonDisabled = false;
      this.loadTaskById(this.selectedTask.id, true);
      this.$toastr('success', message);
      this.hide();
    },
    failed(error) {
      this.buttonDisabled = false;
      this.evidenceFiles = [];
      this.workingPaperFiles = [];
      let message = error.data && error.data.message ? error.data.message : '';
      if (error.data && error.data.errors && error.data.errors['document.0']) {
        message += ' ' + error.data.errors['document.0'][0];
      }
      this.error = message;
    },
    linkSelected() {
      let self = this;
      // Evidence (library files and suggested documentation collection files) is linked as documentation collection
      // attachments; suggested main task files (working papers) stay main task attachments
      const evidenceIds = _.map(this.libraryFiles, 'id').concat(_.map(this.allLinkedDataMediaFiles || [], 'id'));
      const linkedDataMedia = _.filter(this.selectedIds, id => evidenceIds.includes(id));
      const linkedMedia = _.filter(this.selectedIds, id => !evidenceIds.includes(id));

      let payload = {};
      if (linkedDataMedia.length) {
        payload.linked_data_media = linkedDataMedia;
      }
      if (linkedMedia.length) {
        payload.linked_media = linkedMedia;
      }

      this.buttonDisabled = true;
      piincHttp.put('tasks/' + this.selectedTask.id, payload).then(function() {
        self.done(self.$t('tasks.add_attachment.linked_success'));
      }, function(error) {
        self.failed(error);
      });
    },
    filesFormData(files) {
      let formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        if (files[i].size > 31457280) {
          this.error = this.$t('system.file_size_30');
          return null;
        }
        formData.append('document[]', files[i].file);
      }
      return formData;
    },
    uploadEvidence() {
      let self = this;
      if (!this.evidenceFiles.length) {
        return;
      }
      const formData = this.filesFormData(this.evidenceFiles);
      if (!formData) {
        this.evidenceFiles = [];
        return;
      }

      // New evidence goes into the evidence library and is linked to this task
      this.buttonDisabled = true;
      piincHttp.post('tasks/' + this.selectedTask.id + '/evidence', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(function() {
        self.done(self.$t('maintenance.third_parties.media_add_success'));
      }, function(error) {
        self.failed(error);
      });
    },
    uploadWorkingPaper() {
      let self = this;
      if (!this.workingPaperFiles.length) {
        return;
      }
      const formData = this.filesFormData(this.workingPaperFiles);
      if (!formData) {
        this.workingPaperFiles = [];
        return;
      }

      // Working papers stay on the main task only and are never shown to the client
      formData.append('name', 'documents');
      formData.append('collection', 'documents');
      formData.append('model_type', this.selectedTask.who_am_i);
      formData.append('model_id', this.selectedTask.id);

      this.buttonDisabled = true;
      piincHttp.post('documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(function() {
        self.done(self.$t('maintenance.third_parties.media_add_success'));
      }, function(error) {
        self.failed(error);
      });
    }
  }
};
</script>
